import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import api from '../config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Building2, Phone, ArrowLeft, ExternalLink, Linkedin, Users, Edit, Save, X, Globe, Mail, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingContact, setEditingContact] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [tempStatus, setTempStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    mode: 'onBlur'
  });

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await api.get(`/api/companies/${id}`);
        setCompany(response.data.company);
        setContacts(response.data.contacts || []);
        setInteractions(response.data.interactions || []);
        setTempStatus(response.data.company.status || 'New');
      } catch (error) {
        console.error('Error fetching company:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [id]);





  const formatLinkedInUrl = (url) => {
    if (!url) return null;
    
    // Ensure URL has protocol
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    return url;
  };

  const openLinkedIn = (url) => {
    const formattedUrl = formatLinkedInUrl(url);
    if (formattedUrl) {
      window.open(formattedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const openCompanyUrl = (url) => {
    if (url) {
      const fullUrl = url.startsWith('http') ? url : `https://www.importyeti.com/${url}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      
      // Only include fields that have actual values
      const cleanedData = Object.keys(data).reduce((acc, key) => {
        const value = data[key];
        if (value && typeof value === 'string' && value.trim() !== '') {
          acc[key] = value.trim();
        }
        // Don't include empty or null values in the request
        return acc;
      }, {});
      
      const response = await api.put(`/api/companies/${id}`, cleanedData);
      setCompany(response.data.company);
      setEditingContact(false);
      toast.success('Company contact information updated successfully!');
    } catch (error) {
      console.error('Error updating company:', error);
      const message = error.response?.data?.error || 'Failed to update company';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setEditingContact(true);
    setValue('website', company.website || '');
    setValue('phone', company.phone || '');
    setValue('email', company.email || '');
    setValue('notes', company.notes || '');
    setValue('status', company.status || 'New');
  };

  const handleCancel = () => {
    setEditingContact(false);
    reset();
  };

  const handleStatusUpdate = async () => {
    try {
      setUpdatingStatus(true);
      
      const response = await api.put(`/api/companies/${id}`, { status: tempStatus });
      setCompany(response.data.company);
      setEditingStatus(false);
      toast.success('Company status updated successfully!');
    } catch (error) {
      console.error('Error updating company status:', error);
      const message = error.response?.data?.error || 'Failed to update company status';
      toast.error(message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await api.delete(`/api/companies/${id}`);
      toast.success('Company deleted successfully!');
      navigate('/companies');
    } catch (error) {
      console.error('Error deleting company:', error);
      const message = error.response?.data?.error || 'Failed to delete company';
      toast.error(message);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-medium text-gray-900 mb-2">Company not found</h2>
        <Link to="/companies" className="text-blue-600 hover:text-blue-800">
          Back to Companies
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/companies" className="btn btn-outline btn-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Companies
          </Link>
        </div>
        
                {user?.role === 'admin' ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn btn-danger btn-sm"
            disabled={deleting}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Company
          </button>
        ) : (
          <div className="text-sm text-gray-500">
            User role: {user?.role || 'unknown'}
          </div>
        )}
      </div>

      <div className="page-header">
        <h1 className="page-title">{company.name}</h1>
        <p className="page-subtitle">Company details and information</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Company Information</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Company Type</label>
                  <p className="text-gray-900">{company.type || 'N/A'}</p>
                </div>
                <div>
                  <label className="form-label">Country</label>
                  <p className="text-gray-900">{company.country_code || 'N/A'}</p>
                </div>
                <div>
                  <label className="form-label">Region</label>
                  <p className="text-gray-900">
                    {company.region ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        {company.region}
                      </span>
                    ) : 'N/A'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="form-label">Address</label>
                  <p className="text-gray-900">{company.address || 'N/A'}</p>
                </div>
                <div>
                  <label className="form-label">Total Shipments</label>
                  <p className="text-gray-900">{company.total_shipments || 0}</p>
                </div>
                <div>
                  <label className="form-label">Most Recent Shipment</label>
                  <p className="text-gray-900">
                    {company.most_recent_shipment 
                      ? new Date(company.most_recent_shipment).toLocaleDateString()
                      : 'N/A'
                    }
                  </p>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900">{company.status || 'New'}</span>
                    <button
                      onClick={() => setEditingStatus(true)}
                      className="btn btn-outline btn-xs"
                      title="Change status"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                  </div>
                  {editingStatus && (
                    <div className="mt-2 flex items-center gap-2">
                      <select
                        value={tempStatus}
                        onChange={(e) => setTempStatus(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        <option value="New">New</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Hot Lead">Hot Lead</option>
                        <option value="In Discussions">In Discussions</option>
                        <option value="Customer">Customer</option>
                        <option value="Decision-maker Not Found">Decision-maker Not Found</option>
                        <option value="Dead Lead">Dead Lead</option>
                        <option value="Contacts Populated">Contacts Populated</option>
                        <option value="Out Of Area">Out Of Area</option>
                      </select>
                      <button
                        onClick={handleStatusUpdate}
                        disabled={updatingStatus}
                        className="btn btn-primary btn-xs"
                      >
                        {updatingStatus ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        ) : (
                          'Update'
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setEditingStatus(false);
                          setTempStatus(company.status || 'New');
                        }}
                        className="btn btn-outline btn-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                {company.url && (
                  <div className="md:col-span-2">
                    <label className="form-label">ImportYeti Profile</label>
                    <button
                      onClick={() => openCompanyUrl(company.url)}
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                      title="View on ImportYeti"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      <span>View on ImportYeti</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
                {!editingContact && (
                  <button
                    onClick={handleEdit}
                    className="btn btn-outline btn-sm"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                )}
              </div>
            </div>
            <div className="card-body">
              {editingContact ? (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Website</label>
                      <input
                        type="text"
                        {...register('website', {
                          validate: (value) => {
                            if (value && value.trim() !== '') {
                              // Allow partial URLs like example.com or full URLs
                              const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
                              if (!urlPattern.test(value.trim())) {
                                return 'Please enter a valid website URL (e.g., example.com)';
                              }
                            }
                            return true;
                          }
                        })}
                        className="form-input"
                        placeholder="example.com or https://example.com"
                      />
                      {errors.website && (
                        <p className="text-red-500 text-sm mt-1">{errors.website.message}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="form-label">Phone</label>
                      <input
                        type="text"
                        {...register('phone')}
                        className="form-input"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    
                    <div>
                      <label className="form-label">Email</label>
                      <input
                        type="text"
                        {...register('email', {
                          validate: (value) => {
                            if (value && value.trim() !== '') {
                              const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                              if (!emailPattern.test(value.trim())) {
                                return 'Please enter a valid email address';
                              }
                            }
                            return true;
                          }
                        })}
                        className="form-input"
                        placeholder="contact@company.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="form-label">Notes</label>
                    <textarea
                      {...register('notes')}
                      className="form-textarea"
                      rows="4"
                      placeholder="Additional notes about the company..."
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Status</label>
                    <select
                      {...register('status')}
                      className="form-select"
                    >
                      <option value="New">New</option>
                      <option value="Contacted">Contacted</option>
                      <option value="Hot Lead">Hot Lead</option>
                      <option value="In Discussions">In Discussions</option>
                      <option value="Customer">Customer</option>
                      <option value="Decision-maker Not Found">Decision-maker Not Found</option>
                      <option value="Dead Lead">Dead Lead</option>
                      <option value="Contacts Populated">Contacts Populated</option>
                      <option value="Out Of Area">Out Of Area</option>
                    </select>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={saving}
                      className="btn btn-primary"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="btn btn-outline"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Website</label>
                    <p className="text-gray-900">
                      {company.website ? (
                        <a
                          href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          <Globe className="h-4 w-4 mr-1" />
                          {company.website}
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      ) : 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="form-label">Phone</label>
                    <p className="text-gray-900">
                      {company.phone ? (
                        <a
                          href={`tel:${company.phone}`}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          <Phone className="h-4 w-4 mr-1" />
                          {company.phone}
                        </a>
                      ) : 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="form-label">Email</label>
                    <p className="text-gray-900">
                      {company.email ? (
                        <a
                          href={`mailto:${company.email}`}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          {company.email}
                        </a>
                      ) : 'N/A'}
                    </p>
                  </div>
                  
                  {company.notes && (
                    <div className="md:col-span-2">
                      <label className="form-label">Notes</label>
                      <p className="text-gray-900 whitespace-pre-wrap">{company.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Contacts */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Contacts</h2>
            </div>
            <div className="card-body">
              {contacts && contacts.length > 0 ? (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-medium text-gray-900">
                              {contact.first_name} {contact.last_name}
                            </h3>
                            {contact.linkedin_url && (
                              <button
                                onClick={() => openLinkedIn(contact.linkedin_url)}
                                className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                                title="View LinkedIn Profile"
                              >
                                <Linkedin className="h-4 w-4 mr-1" />
                                <span className="text-sm">LinkedIn</span>
                                <ExternalLink className="h-3 w-3 ml-1" />
                              </button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                            {contact.position && (
                              <div className="flex items-center">
                                <Users className="h-4 w-4 mr-2 text-gray-400" />
                                <span className="font-medium">Position:</span>
                                <span className="ml-2">{contact.position}</span>
                              </div>
                            )}
                            {contact.department && (
                              <div className="flex items-center">
                                <Building2 className="h-4 w-4 mr-2 text-blue-600" />
                                <span className="font-medium">Department:</span>
                                <span className="ml-2">{contact.department}</span>
                              </div>
                            )}
                            {contact.email && (
                              <div className="flex items-center">
                                <span className="font-medium">Email:</span>
                                <a 
                                  href={`mailto:${contact.email}`}
                                  className="ml-2 text-blue-600 hover:text-blue-800"
                                >
                                  {contact.email}
                                </a>
                              </div>
                            )}
                            {contact.phone && (
                              <div className="flex items-center">
                                <Phone className="h-4 w-4 mr-2 text-gray-400" />
                                <span className="font-medium">Phone:</span>
                                <a 
                                  href={`tel:${contact.phone}`}
                                  className="ml-2 text-blue-600 hover:text-blue-800"
                                >
                                  {contact.phone}
                                </a>
                              </div>
                            )}
                          </div>
                          
                          {contact.notes && (
                            <div className="mt-3 p-3 bg-white rounded border-l-4 border-blue-200">
                              <p className="text-sm text-gray-700">{contact.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No contacts found for this company</p>
              )}
            </div>
          </div>

          {/* Recent Interactions */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Recent Interactions</h2>
            </div>
            <div className="card-body">
              {interactions && interactions.length > 0 ? (
                <div className="space-y-3">
                  {interactions.map((interaction) => (
                    <div key={interaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">
                          {interaction.type} - {interaction.method}
                        </p>
                        {interaction.subject && (
                          <p className="text-sm text-gray-600">{interaction.subject}</p>
                        )}
                        <p className="text-xs text-gray-500">
                          {new Date(interaction.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No interactions found for this company</p>
              )}
            </div>
          </div>

          {/* Suppliers */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Top Suppliers</h2>
            </div>
            <div className="card-body">
              {company.suppliers && company.suppliers.length > 0 ? (
                <div className="space-y-3">
                  {company.suppliers.map((supplier, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-sm font-semibold text-green-600">{index + 1}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{supplier}</p>
                          <p className="text-sm text-gray-500">Supplier</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          Top Supplier
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building2 className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No suppliers found</h3>
                  <p className="text-gray-500">This company doesn't have any suppliers listed yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <Link to={`/interactions?company=${company.id}`} className="btn btn-blue w-full">
                  <Phone className="h-4 w-4 mr-2" />
                  Log Interaction
                </Link>
                <Link to={`/contacts?company=${company.id}`} className="btn btn-blue w-full">
                  <Users className="h-4 w-4 mr-2" />
                  Add Contact
                </Link>
              </div>
            </div>
          </div>

          {/* Company Stats */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">Company Stats</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Contacts:</span>
                  <span className="font-medium">{contacts?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Interactions:</span>
                  <span className="font-medium">{interactions?.length || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="flex items-center mb-4">
              <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Company</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{company?.name}</strong>? This action cannot be undone and will also delete all associated contacts and interactions.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-outline flex-1"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger flex-1"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDetail;
